import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Typography, Alert, Space, Tabs, Badge, Button, ConfigProvider, Segmented, Popconfirm } from 'antd';
import { BellOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import { LangContext, useI18n } from './i18n';
import api, { getToken, clearToken, setUnauthorizedHandler } from './api';
import WalletList from './components/WalletList';
import SwapForm from './components/SwapForm';
import TransactionHistory from './components/TransactionHistory';
import Settings from './components/Settings';
import Notifications from './components/Notifications';
import Login from './components/Login';

const { Header, Content } = Layout;

// 顶层：语言状态（localStorage 持久化）+ antd 语言包 + 登录门禁
function App() {
  const [lang, setLangState] = useState(() => localStorage.getItem('lang') || 'zh');
  const setLang = (l) => {
    localStorage.setItem('lang', l);
    setLangState(l);
  };
  // 登录态：有 token 即视为已登录（接口 401 会自动清 token 并回登录页）
  const [authed, setAuthed] = useState(() => Boolean(getToken()));
  const [username, setUsername] = useState(() => localStorage.getItem('auth_user') || '');

  // 注册全局 401 处理：任一接口未授权即登出回登录页
  useEffect(() => {
    setUnauthorizedHandler(() => setAuthed(false));
  }, []);

  const handleLoggedIn = (data) => {
    localStorage.setItem('auth_user', data.username || '');
    setUsername(data.username || '');
    setAuthed(true);
  };
  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('auth_user');
    setAuthed(false);
  };

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      <ConfigProvider locale={lang === 'en' ? enUS : zhCN}>
        {authed ? (
          <Main username={username} onLogout={handleLogout} />
        ) : (
          <Login onLoggedIn={handleLoggedIn} />
        )}
      </ConfigProvider>
    </LangContext.Provider>
  );
}

function Main({ username, onLogout }) {
  const { t, ts, lang, setLang } = useI18n();
  const [wallets, setWallets] = useState([]);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [error, setError] = useState('');
  // 用计数器通知交易记录组件刷新
  const [refreshTick, setRefreshTick] = useState(0);
  // 当前激活的页面 tab；"查看交易"会切到交易记录并聚焦对应钱包
  const [activeTab, setActiveTab] = useState('wallets');
  // {address, tick}：tick 保证连续点击同一钱包也能重新触发聚焦
  const [viewTx, setViewTx] = useState(null);
  // 地址簿：自定义地址名称，用于全站地址显示增强
  const [addressBook, setAddressBook] = useState([]);
  // 站内消息未读数（header 铃铛角标）与消息页显示状态
  const [unread, setUnread] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  // 未读数轮询：自动交易在后台运行，异常要及时在角标上体现
  const fetchUnread = useCallback(async () => {
    try {
      const res = await api.get('/notifications?limit=1');
      setUnread(res.data.data.unread);
    } catch {
      // 静默失败，下个周期重试
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const timer = setInterval(fetchUnread, 60 * 1000);
    return () => clearInterval(timer);
  }, [fetchUnread]);

  const fetchWallets = useCallback(async () => {
    setLoadingWallets(true);
    setError('');
    try {
      const res = await api.get('/wallets');
      setWallets(res.data.data);
    } catch (err) {
      setError(t('加载钱包失败: ') + ts(err.response?.data?.error || err.message));
    } finally {
      setLoadingWallets(false);
    }
  }, [t, ts]);

  const fetchAddressBook = useCallback(async () => {
    try {
      const res = await api.get('/address-book');
      setAddressBook(res.data.data);
    } catch (err) {
      // 地址簿加载失败不阻塞主功能，仅记录
      console.warn('地址簿加载失败:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchWallets();
    fetchAddressBook();
  }, [fetchWallets, fetchAddressBook]);

  // swap 完成后刷新余额与记录
  const handleSwapDone = () => {
    fetchWallets();
    setRefreshTick((n) => n + 1);
  };

  // 钱包列表"查看交易"：切到交易记录 tab 并聚焦该钱包的链上记录
  const handleViewTransactions = (address) => {
    setViewTx((prev) => ({ address, tick: (prev?.tick || 0) + 1 }));
    setActiveTab('transactions');
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ display: 'flex', alignItems: 'center' }}>
        <Typography.Title level={4} style={{ color: '#fff', margin: 0 }}>
          Swap Bot · BSC
        </Typography.Title>
        <Typography.Text style={{ color: 'rgba(255,255,255,0.65)', marginLeft: 16 }}>
          USDT ⇄ USGNAA（PancakeSwap）
        </Typography.Text>
        {/* 右上角：语言切换 + 消息铃铛（未读角标） + 当前用户 + 登出 */}
        <Space style={{ marginLeft: 'auto' }} size={16}>
          <Segmented
            size="small"
            value={lang}
            onChange={setLang}
            options={[
              { label: '中', value: 'zh' },
              { label: 'EN', value: 'en' }
            ]}
          />
          <Badge count={unread} size="small" offset={[-2, 4]}>
            <Button
              type="text"
              icon={<BellOutlined style={{ fontSize: 18, color: '#fff' }} />}
              onClick={() => setShowNotifications(true)}
            />
          </Badge>
          {username && (
            <Typography.Text style={{ color: 'rgba(255,255,255,0.85)' }}>
              <UserOutlined /> {username}
            </Typography.Text>
          )}
          <Popconfirm
            title={t('确认登出？')}
            okText={t('登出')}
            cancelText={t('取消')}
            onConfirm={onLogout}
          >
            <Button type="text" icon={<LogoutOutlined style={{ color: '#fff' }} />} />
          </Popconfirm>
        </Space>
      </Header>
      <Content style={{ padding: 24, maxWidth: 1200, width: '100%', margin: '0 auto' }}>
        {showNotifications ? (
          // 独立消息页面（隐藏主界面，返回后恢复原 tab 状态）
          <Notifications onUnreadChange={setUnread} onBack={() => setShowNotifications(false)} />
        ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} />}
          <SwapForm wallets={wallets} onSwapDone={handleSwapDone} />
          {/* 钱包与交易记录分 tab 展示，随机 Swap 卡片保持常驻 */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            size="large"
            items={[
              {
                key: 'wallets',
                label: t('钱包'),
                children: (
                  <WalletList
                    wallets={wallets}
                    loading={loadingWallets}
                    onRefresh={fetchWallets}
                    onViewTransactions={handleViewTransactions}
                    addressBook={addressBook}
                  />
                )
              },
              {
                key: 'transactions',
                label: t('交易记录'),
                children: (
                  <TransactionHistory
                    wallets={wallets}
                    refreshTick={refreshTick}
                    focus={viewTx}
                    addressBook={addressBook}
                  />
                )
              },
              {
                key: 'settings',
                label: t('设置'),
                children: <Settings addressBook={addressBook} onAddressBookChanged={fetchAddressBook} />
              }
            ]}
          />
        </Space>
        )}
      </Content>
    </Layout>
  );
}

export default App;
